import _, { floor, isNil, reduce, trim } from 'lodash';
import numeral from 'numeral';
import React, { Fragment } from 'react';
import { TableColumn } from 'react-data-table-component';
import { FilterDescription } from '../hooks/useFilters';
import styled from '../themes/themed-styled-components';
import { adrClassesList, unNumbersList } from '../util';
import { BooleanValue, DateTime as DateTimeEl, LoadingUnitIdentifier } from './../components';
import { DangerousGoods, Handling, HandlingType, HarbourOfficeAuthorizationType, Terminal, User } from './../types';

export type Column<T> = TableColumn<T>;

export type HandlingColumn = Column<HandlingRow>;

export type DangerousGoodsColumn = Column<DangerousGoodsRow>;

const Cell = styled.div.attrs({ 'data-tag': 'allowRowEvents' })`
    position:relative;
    &:before {
        content: " ";
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        z-index: 1;
        user-select: none;
    }
`;

export const handlingColumns: Partial<Record<keyof HandlingRow, Partial<HandlingColumn> & { filter?: Omit<FilterDescription<HandlingRow, keyof HandlingRow>, 'field' | 'title'> }>> = {
    id: {
        sortable: true,
        omit: process.env.NODE_ENV !== 'development'
    },
    loadingUnitIdentifier: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><LoadingUnitIdentifier identifier={row.loadingUnitIdentifier as string} /></Cell>,
        filter: { type: 'string' }
    },
    shippingAgencyOrCarrier: {
        sortable: true,
        wrap: true,
        filter: { type: 'list' }
    },
    voyage: {
        sortable: true,
        filter: { type: 'list' }
    },
    ship: {
        sortable: true,
        filter: { type: 'list' }
    },
    unboardingShip: {
        selector: (row: HandlingRow | null) => {
            if (isNil(row) || isNil(row.source)) {
                return '';
            }

            const { unboardingShipFromTerminal, unboardingShip } = row.source;

            return (isNil(unboardingShipFromTerminal) || trim(unboardingShipFromTerminal).length === 0 ? unboardingShip : unboardingShipFromTerminal) ?? ''
        },
        sortable: true,
        filter: { type: 'list' }
    },
    boardingShip: {
        selector: (row: HandlingRow | null) => {
            if (isNil(row) || isNil(row.source)) {
                return '';
            }

            const { boardingShipFromTerminal, boardingShip } = row.source;

            return (isNil(boardingShipFromTerminal) || trim(boardingShipFromTerminal).length === 0 ? boardingShip : boardingShipFromTerminal) ?? ''
        },
        sortable: true,
        filter: { type: 'list' }
    },
    idSerchim: {
        sortable: true,
        filter: { type: 'string' }
    },
    booking: {
        width: "16em",
        sortable: true,
    },
    position: { sortable: true },
    area: {
        sortable: true,
        width: "5em",
        filter: { type: 'list' }
    },
    bay: {
        sortable: true,
        width: "60px",
        filter: { type: 'list' }
    },
    row: {
        sortable: true,
        width: "60px",
        filter: { type: 'list' }
    },
    tier: {
        sortable: true,
        width: "60px",
        filter: { type: 'list' }
    },
    checkInDate: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><DateTimeEl date={row.checkInDate} /></Cell>,
        filter: { type: 'date' }
    },
    checkOutDate: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><DateTimeEl date={row.checkOutDate} /></Cell>,
        filter: { type: 'date' }
    },
    creationDate: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><DateTimeEl date={row?.creationDate} /></Cell>,
        filter: { type: 'date' }
    },
    equipmentType: {
        sortable: true,
        filter: { type: 'string' }
    },
    unNumbers: {
        format: (row: HandlingRow | null) => isNil(row) ? null : _(row?.unNumbers ?? []).join(' '),
        wrap: true,
        cell: (row: HandlingRow | null) => <Cell>{
            unNumbersList(row?.source?.dangerousGoods ?? []).map(([unNumber, limitedQuantity], i) => <Fragment key={unNumber}>
                {i === 0 ? '' : ','} {
                    limitedQuantity
                        ? <span title="Limited Quantity">{unNumber}</span>
                        : <strong>{unNumber}</strong>}
            </Fragment>)
        }</Cell>,
        filter: { type: 'list' }
    },
    adrClasses: {
        format: (row: HandlingRow | null) => isNil(row) ? null : _(row?.adrClasses ?? []).join(' '),
        cell: (row: HandlingRow | null) => <Cell>{
            adrClassesList(row?.source?.dangerousGoods ?? []).map(([adrClass, limitedQuantity], i) => <Fragment key={adrClass}>
                {i === 0 ? '' : ','} {
                    limitedQuantity
                        ? <span title="Limited Quantity">{adrClass}</span>
                        : <strong>{adrClass}</strong>}
            </Fragment>)
        }</Cell>,
        filter: { type: 'list' }
    },
    qsaNotesOrPrescriptions: {
        sortable: true,
        selector: (row: HandlingRow | null) => {
            const source = row?.source
            if (isNil(source)) {
                return ''
            }

            const qsaNotes = source.qsaNotes;

            if (!isNil(qsaNotes) && qsaNotes.trim().length > 0) {
                return qsaNotes
            }

            return source.prescriptions ?? ''
        }
    },
    totalWeight: {
        sortable: true,
        format: (row: HandlingRow | null) => {
            if (isNil(row)) {
                return;
            }
            const weight = row.totalWeight;
            return weight === null ? null : `${numeral(row?.totalWeight).format('0[.]00')} kg`
        },
        filter: { type: 'number' }
    },
    portAuthorityAuthorizationFileNumber: {
        sortable: true
    },
    status: {
        sortable: true,
        omit: true
    },
    dangerousGoodsPackaging: {
        format: (row: HandlingRow | null) => isNil(row) ? null : _(row?.dangerousGoodsPackaging ?? []).filter(p => p.trim().length > 0).join(', '),
        sortable: true,
        width: "240px",
        filter: { type: 'list' }
    },
    direct: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><BooleanValue value={row.direct ?? null} /></Cell>,
        filter: { type: 'boolean' }
    },
    approvedByTerminal: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><BooleanValue value={row.approvedByTerminal ?? null} /></Cell>,
        filter: { type: 'boolean' }
    },
    checkedByTerminal: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><BooleanValue value={row.checkedByTerminal ?? false} /></Cell>,
        filter: { type: 'boolean' }
    },
    approvedByAuthority: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><BooleanValue value={row.approvedByAuthority ?? false} /></Cell>,
        filter: { type: 'boolean' }
    },
    approvedByHarbourOffice: {
        sortable: true,
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><BooleanValue value={row.approvedByHarbourOffice ?? null} /></Cell>,
        filter: { type: 'boolean' }
    },
    limitedQuantity: {
        cell: (row: HandlingRow | null) => isNil(row) ? null : <Cell><BooleanValue value={row.limitedQuantity ?? false} /></Cell>,
        sortable: true,
        filter: { type: 'boolean' }
    },
    assignedDate: {
        sortFunction: ({ source: src1 }, { source: src2 }) => {

            const ad1 = src1?.assignedDate ?? null
            const ad2 = src2?.assignedDate ?? null

            const dt1 = ad1 === null ? 0 : new Date(ad1).getTime();
            const dt2 = ad2 === null ? 0 : new Date(ad2).getTime();

            return dt1 - dt2
        },
        cell: ({ source }) => {
            const assignedDate = source?.assignedDate

            //const eta =  DateTime.fromISO(source?.shipArrival.eta!).toJSDate()
            //const assignedDate = isNil(ad) ? null : DateTime.fromISO(ad!).toJSDate()

            return isNil(assignedDate) ? '-' : <DateTimeEl date={assignedDate} />
        },
        sortable: true,
        filter: { type: 'date' }
    },
    requestedDate: {
        sortFunction: ({ source: src1 }, { source: src2 }) => {

            const ad1 = src1?.requestedDate ?? null
            const ad2 = src2?.requestedDate ?? null

            const dt1 = ad1 === null ? 0 : new Date(ad1).getTime();
            const dt2 = ad2 === null ? 0 : new Date(ad2).getTime();

            return dt1 - dt2
        },
        cell: ({ source }) => {
            const requestedDate = source?.requestedDate

            //const eta =  DateTime.fromISO(source?.shipArrival.eta!).toJSDate()
            //const assignedDate = isNil(ad) ? null : DateTime.fromISO(ad!).toJSDate()

            return isNil(requestedDate)
                ? '-'
                : <DateTimeEl date={requestedDate} />
        },
        sortable: true,
        filter: { type: 'date' }
    },

    storageDays: {
        sortable: true,
        filter: { type: 'number' }
    },
    
    terminalVoyageEta: {
        sortFunction: ({ source: src1 }, { source: src2 }) => {

            const ad1 = src1?.terminalVoyageEta ?? null
            const ad2 = src2?.terminalVoyageEta ?? null

            const dt1 = ad1 === null ? 0 : new Date(ad1).getTime();
            const dt2 = ad2 === null ? 0 : new Date(ad2).getTime();

            return dt1 - dt2
        },
        cell: ({ source }) => {
            const terminalVoyageEta = source?.terminalVoyageEta

            //const eta =  DateTime.fromISO(source?.shipArrival.eta!).toJSDate()
            //const assignedDate = isNil(ad) ? null : DateTime.fromISO(ad!).toJSDate()

            return isNil(terminalVoyageEta)
                ? '-'
                : <DateTimeEl date={terminalVoyageEta} />
        },
        sortable: true,
        filter: { type: 'date' }
    }
}
//as Record<string, Partial<HandlingColumn>>).reduce((cols, col, key) => {
//        cols[key] = defaults({
//            name: t([`terminal.data.handlings.columns.${key}.name`, `terminal.yard.data.columns.${key}.name`, `terminal.data.columns.${key}.name`, `data.columns.${key}.name`]),
//            selector: col.selector ?? key
//        }, col) as IDataTableColumn<HandlingRow>;

//        return cols;
//    }, {}) as Record<string, HandlingColumn>;
//}

//const emergencyDataSheetUrlTemplate = template(process.env.REACT_APP_EMERGENCY_DATASHEETS_URL_TMPL)

export const dangerousGoodsColumns: Partial<Record<keyof DangerousGoodsRow, Partial<DangerousGoodsColumn>>> = {
    id: {
        sortable: true,
        omit: process.env.NODE_ENV !== 'development'
    },
    adrClass: { sortable: true },
    limitedQuantity: {
        cell: (row: DangerousGoodsRow | null) => isNil(row) ? null : <Cell><BooleanValue value={row.limitedQuantity ?? null} /></Cell>,
        sortable: true
    },
    marinePollutant: {
        cell: (row: DangerousGoodsRow | null) => isNil(row) ? null : <Cell><BooleanValue value={row.marinePollutant ?? null} /></Cell>,
        sortable: true
    },
    packaging: {
        sortable: true
    },
    packingGroup: {
        sortable: true
    },
    unNumber: {
        sortable: true
    },
    weight: {
        sortable: true,
        cell: (row: DangerousGoodsRow | null) => {
            if (isNil(row)) return null;
            return <Cell>{`${numeral(row.weight).format('0[.]00')}`} kg</Cell>;
        }
    },
    attachments: {
        sortable: false,
        cell: (row: DangerousGoodsRow | null) => {
            if (isNil(row)) return null;

            const attachments = row.attachments ?? null;

            if (attachments === null) {
                return null;
            }

            const { safetyDataSheetUrl, emergencyCardUrl } = attachments;

            if (safetyDataSheetUrl !== null) {
                return <a target="_blank" rel="noreferrer" href={safetyDataSheetUrl as string}>Scheda di sicurezza</a>;
            }

            if (emergencyCardUrl !== null) {
                return <a target="_blank" rel="noreferrer" href={emergencyCardUrl}>Scheda di emergenza</a>
            }

            return null;
        }
    }
};

//    } as Record<string, Partial<DangerousGoodsColumn>>).reduce((cols, col, key) => {
//        cols[key] = defaults({
//            name: t([`terminal.data.dangerousGoods.columns.${key}.name`, `terminal.yard.data.columns.${key}.name`, `terminal.data.columns.${key}.name`, `data.columns.${key}.name`]),
//            selector: col.selector ?? key
//        }, col) as IDataTableColumn<DangerousGoods>;

//        return cols;
//    }, {}) as Record<string, IDataTableColumn<DangerousGoods>>;
//}

export type Row<T> = T & {
    source?: T,
    isActive?: boolean
}

export type Context = {
    user?: User,
    terminal?: Terminal,
    history?: unknown,
    url?: string
};

export type Converter<T, TRow extends Row<T>> = {
    [k in keyof TRow]?: (source: T, context: Context) => TRow[k]
}

export enum HandlingStatus {
    None = 'none',
    Approved = 'approved',
    ApprovedWithPrescriptions = 'approvedWithPrescriptions',
    ApprovedByTerminal = 'approvedByTerminal',
    NotYetAuthorized = 'notYetAuthorized',
    NotYetViewed = 'notYetViewed',
    Direct = 'direct',
    MissingData = 'missingData'
}

export type HandlingRow = Row<Handling> & {
    id: number,
    loadingUnitIdentifier?: string | null,
    shippingAgencyOrCarrier?: string | null,
    booking?: string | null,
    idSerchim?: number | null,
    voyage?: string | null,
    ship?: string | null,
    unboardingShip?: string | null,
    boardingShip?: string | null,
    position?: string | null,
    area?: string | null,
    bay?: string | null,
    row?: string | null,
    tier?: string | null,
    checkInDate?: Date | null,
    checkOutDate?: Date | null,
    creationDate?: Date | null,
    equipmentType?: string | null,
    cei?: number | null,
    fei?: number | null,
    standardDistance?: number | null,
    unNumbers?: string[],
    adrClasses?: string[],
    totalWeight?: number | null,
    qsaNotesOrPrescriptions?: string | null,
    portAuthorityAuthorizationFileNumber?: number | null,
    dangerousGoodsPackaging?: string[] | null,
    status?: HandlingStatus,
    direct?: boolean,
    approval?: string | null,
    approvedByTerminal?: boolean,
    checkedByTerminal?: boolean,
    approvedByAuthority?: boolean,
    approvedByHarbourOffice?: boolean,
    limitedQuantity?: boolean,
    assignedDate?: Date | null,
    storageDays?: number | null,
    terminalVoyageEta?: Date | null,
}

export const handlingConverter: Converter<Handling, HandlingRow> = {
    position: ({ area, bay, row, tier }) => `${area ?? '-'} ${bay ?? '-'} ${row ?? '-'} ${tier ?? '-'}`,

    unNumbers: ({ dangerousGoods }) => (_(dangerousGoods ?? []).map('unNumber').uniq().sort().value() as string[]),

    adrClasses: ({ dangerousGoods }) => (_(dangerousGoods ?? []).map('adrClass').uniq().sort().value() as string[]),

    totalWeight: ({ dangerousGoods }) => {
        const dg = dangerousGoods ?? [];
        if (dg.length === 0) {
            return null;
        }
        return _(dg).sumBy('weight');
    },

    portAuthorityAuthorizationFileNumber: ({ portAuthorityAuthorization }) => (portAuthorityAuthorization?.fileNumber ?? null),

    approvedByAuthority: ({ portAuthorityAuthorization }) => (portAuthorityAuthorization?.authorized ?? undefined),

    approvedByHarbourOffice: ({ harbourOfficeAuthorization, type }) => {
        if (harbourOfficeAuthorization !== null) {
            switch (harbourOfficeAuthorization?.authorization) {
                case HarbourOfficeAuthorizationType.Boarding:
                    return type === HandlingType.Boarding || type === HandlingType.TranshipmentBoarding;
                case HarbourOfficeAuthorizationType.Unboarding:
                    return type === HandlingType.Unboarding || type === HandlingType.TranshipmentUnboarding;
                case HarbourOfficeAuthorizationType.Transit:
                    return type === HandlingType.Transit;
            }
        }

        return false;
    },

    storageDays: ({ storageDays }) => floor(storageDays as number),

    

    limitedQuantity: ({ dangerousGoods }) => dangerousGoods?.every(({ limitedQuantity }) => limitedQuantity) ?? false,

    dangerousGoodsPackaging: ({ dangerousGoods }) => (_(dangerousGoods ?? []).map('packaging').uniq().sort().value() as string[]),

    status: ({ dangerousGoods, portAuthorityAuthorization, harbourOfficeAuthorization, type, direct, approvedByTerminal, checkedByTerminal, prescriptions, goodsDataModified }, { terminal }) => {

        if (dangerousGoods === undefined) {
            return HandlingStatus.None;
        }

        if ((dangerousGoods ?? []).length === 0) {
            return HandlingStatus.MissingData;
        }

        if (direct === undefined) {
            return HandlingStatus.None;
        }

        if ((direct ?? false) === true) {
            return HandlingStatus.Direct
        }

        if (terminal?.port === 'LIVORNO') {//TODO: Sostituire con impostazioni
            if (portAuthorityAuthorization?.authorized ?? false) {
                if (checkedByTerminal === undefined
                    || approvedByTerminal === undefined) {
                    return HandlingStatus.None;
                }

                const checked = checkedByTerminal;

                if ((approvedByTerminal ?? false) === true) {
                    return checked ? HandlingStatus.ApprovedWithPrescriptions : HandlingStatus.Approved
                } else {
                    return checked ? HandlingStatus.NotYetAuthorized : HandlingStatus.NotYetViewed
                }
            }
            return HandlingStatus.NotYetAuthorized;

        } else if (terminal?.port === 'GENOVA' || terminal?.name === 'LSCT') {


            if (approvedByTerminal === undefined) {
                return HandlingStatus.None;
            }

            if (approvedByTerminal !== true) {
                return HandlingStatus.NotYetAuthorized;
            }

            if (terminal?.name === 'REBORA' && (harbourOfficeAuthorization === undefined || harbourOfficeAuthorization === null)) {
                return HandlingStatus.ApprovedByTerminal;
            }

            return HandlingStatus.Approved;

        } else if (terminal?.port === 'LA SPEZIA') {

            //if (approvedByTerminal !== true) {
            //    return HandlingStatus.NotYetAuthorized;
            //}

            //return HandlingStatus.Approved;

            if (type === HandlingType.Transit) {
                return HandlingStatus.None;
            }

            const hasPrescriptions = !(prescriptions === null || !(prescriptions?.match(/nessuna( prescrizione| particolare|\.)/i) === null));


            const approved = approvedByTerminal && !(goodsDataModified ?? false)

            if (approved === true) {
                return hasPrescriptions ? HandlingStatus.ApprovedWithPrescriptions : HandlingStatus.Approved;
            }

            return HandlingStatus.NotYetAuthorized;


        } else if (terminal?.port === 'VENEZIA') {
            return HandlingStatus.Approved;
        }

        //if (portAuthorityAuthorization === undefined) {
        //    return HandlingStatus.None;
        //}



        return HandlingStatus.None;
    }


};

type DangerousGoodsAttachments = {
    emergencyCardUrl: string | null,
    safetyDataSheetUrl: string | null
}

export type DangerousGoodsRow = Row<DangerousGoods> & {
    id: number,
    unNumber?: string,
    packingGroup?: string,
    weight?: number,
    packaging?: string | null,
    numberOfPackages?: number,
    provision274?: string,
    flashPoint?: string,
    adrClass?: string,
    adrSecondaryClasses?: string[],
    marinePollutant?: boolean,
    limitedQuantity?: boolean,
    fumigated?: boolean,
    coolingAgents?: boolean,
    elevatedTemperature?: boolean,
    emergencyCardUrl?: string,
    attachments?: DangerousGoodsAttachments
}

export type RowKeys<T, TRow> = Exclude<keyof TRow, Row<T>>;

export const dangerousGoodsConverter: Converter<DangerousGoods, DangerousGoodsRow> = {
    attachments: ({ safetyDataSheet, unNumber, adrClass }) => {
        return {
            emergencyCardUrl: `/schede-di-emergenza/${adrClass}/${unNumber}`,
            safetyDataSheetUrl: safetyDataSheet?.downloadUrl ?? null
        } as DangerousGoodsAttachments;
    }
};

export const convertSourceToRow = <T, TRow extends Row<T>>(source: T, context: Context, columns: (RowKeys<T, TRow>)[], converter: Converter<T, TRow>): TRow => {
    return reduce(columns, (row, col) => {
        const conv = converter[col];
        row[col] = (conv === undefined) ? ((source as any)[col]) : (conv?.(source, context));

        return row;
    }, { source } as TRow);
}

const partialConverter = <T, TRow extends Row<T>>(converter: Converter<T, TRow>) => (
    (source: T, context: Context, columns: (Exclude<keyof TRow, Row<T>>)[]) => convertSourceToRow<T, TRow>(source, context, columns, converter)
);


export const convertHandlingToRow = partialConverter(handlingConverter);
export const convertDangerousGoodsToRow = partialConverter(dangerousGoodsConverter);
















